import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db73tav799gd4v2febhg";

const SUBJECTS = ["内容反馈", "合作咨询", "版权相关", "功能建议", "其他"];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setSuccessMsg("感谢你的反馈，我们已经收到你的留言。");
      setStatus("success");
      form.reset();
      return;
    }

    formData.delete("website_alt");
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, value.toString());
    });

    setStatus("submitting");
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        data?: unknown;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setSuccessMsg("感谢你的反馈，我们已经收到你的留言，会尽快回复你。");
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后重试。",
        );
      }
    } catch (error) {
      setStatus("error");
      setFormError(
        error instanceof Error
          ? `网络异常：${error.message}`
          : "网络异常，请检查网络后重试。",
      );
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-background-300 bg-background-100 p-8 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-600">
          <i className="ri-checkbox-circle-line text-3xl" aria-hidden="true" />
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
          提交成功
        </h3>
        <p className="mt-2 text-sm text-foreground-600">{successMsg}</p>
        <button
          type="button"
          onClick={() => {
            setStatus("idle");
            setSuccessMsg("");
          }}
          className="mt-5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
        >
          再写一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-700">
            称呼 <span className="text-accent-600">*</span>
          </span>
          <input
            type="text"
            name="name"
            required
            placeholder="请输入你的称呼"
            className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-700">
            邮箱 <span className="text-accent-600">*</span>
          </span>
          <input
            type="email"
            name="email"
            required
            placeholder="用于接收我们的回复"
            className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground-700">反馈类型</span>
        <div className="relative">
          <select
            name="subject"
            defaultValue={SUBJECTS[0]}
            className="w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 pr-9 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
          >
            {SUBJECTS.map((subject) => (
              <option key={subject} value={subject}>
                {subject}
              </option>
            ))}
          </select>
          <i
            className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
            aria-hidden="true"
          />
        </div>
      </label>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground-700">
          留言内容 <span className="text-accent-600">*</span>
        </span>
        <textarea
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你遇到的问题或建议（最多 500 字）"
          className="resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
        />
      </label>

      <input
        type="text"
        name="website_alt"
        className="form-guard-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" && formError && (
        <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-2.5 text-sm text-accent-900">
          <i className="ri-error-warning-line" aria-hidden="true" />
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {status === "submitting" ? (
          <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-background-50/40 border-t-background-50" />
            提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" aria-hidden="true" />
            提交反馈
          </>
        )}
      </button>
    </form>
  );
}